import React, { useState } from "react";
import { ChevronDown, HelpCircle, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const faqs = [
  {
    question: "What export documents can I generate with Export Stuff?",
    answer:
      "Export Stuff automates your complete international trade documentation flow: Quotations, Proforma Invoices (PI), Commercial Invoices (CI), Container Packing Lists (PL), Bill of Lading (BL) Drafts, Verified Gross Mass (VGM) declarations, and Shipping Annexures engineered specifically for ceramic tile manufacturers and exporters.",
  },
  {
    question: "Does the platform handle ceramic tile calculations like SQM per box and container weight limits?",
    answer:
      "Yes. The platform has native ceramic export calculation logic. When you input tile sizes (e.g., 600x600, 600x1200, 800x1600mm), box packing specs, and pallet configurations, Export Stuff automatically computes total square meters (SQM), boxes, pallet totals, net weight, gross weight, and warns if container payload limits are exceeded.",
  },
  {
    question: "Can I manage multi-currency pricing and international Incoterms (FOB, CIF, CFR)?",
    answer:
      "Absolutely. Export Stuff supports multi-currency documentation (USD, EUR, GBP, AED, INR) alongside Incoterms 2020 rules (FOB Mundra, CIF, CFR, EXW). You can factor ocean freight, marine insurance, and custom exchange rates into documents with one click.",
  },
  {
    question: "Is my organization's pricing and customer database secure from other exporters?",
    answer:
      "Yes. Export Stuff is built with strict multi-tenant data isolation. All customer accounts, buyer directories, pricing discounts, and proprietary document drafts are strictly partitioned by Organization ID and encrypted both in transit (TLS 1.3/HTTPS) and at rest.",
  },
  {
    question: "How does the 14-day free trial work?",
    answer:
      "Our Free Trial gives you immediate access to create up to 20 export documents with 2 team seats for 14 days. No credit card is required to sign up. You can upgrade anytime to the Growth or Enterprise plan from your billing dashboard.",
  },
  {
    question: "Can we customize document numbering series (e.g., EXP/26-27/001) and add our company logo?",
    answer:
      "Yes. You have full control over custom prefix, suffix, and fiscal-year numbering sequences across all document types. Your company logo, bank details, and letterhead styling are automatically integrated into all generated PDFs and Excel packing lists.",
  },
];

export const FaqSection = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  // Structured Data Schema for Google Search Rich Results (FAQPage)
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <section id="faq" className="py-20 bg-slate-50 border-t border-slate-200">
      {/* Inject FAQPage Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center space-y-3 mb-14">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-100 border border-amber-200 text-amber-900 text-xs font-bold tracking-wide uppercase">
            <HelpCircle className="w-3.5 h-3.5 text-amber-700" />
            <span>Frequently Asked Questions</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            Everything You Need to Know About{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-amber-800">
              Export Stuff
            </span>
          </h2>
          <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto">
            Got questions about export documentation workflows, ceramic tile calculations, or plan subscriptions? We have got you covered.
          </p>
        </div>

        {/* Accordion List */}
        <div className="space-y-4">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={index}
                className={`rounded-2xl transition-all duration-200 border ${
                  isOpen
                    ? "bg-white border-amber-300 shadow-md shadow-amber-600/5 ring-1 ring-amber-300/40"
                    : "bg-white border-slate-200 hover:border-amber-200"
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-5 sm:p-6 text-left cursor-pointer focus:outline-hidden"
                  aria-expanded={isOpen}
                >
                  <span className="text-sm sm:text-base font-bold text-slate-900 pr-4">
                    {faq.question}
                  </span>
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-transform duration-200 ${
                      isOpen
                        ? "bg-amber-100 text-amber-800 rotate-180"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </div>
                </button>

                {isOpen && (
                  <div className="px-5 sm:px-6 pb-6 pt-1 text-xs sm:text-sm text-slate-600 leading-relaxed border-t border-slate-100">
                    {faq.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer Support Banner */}
        <div className="mt-12 p-6 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <div className="space-y-1">
            <h4 className="text-sm font-bold text-slate-900">
              Have questions specific to your export operations?
            </h4>
            <p className="text-xs text-slate-600">
              Our export workflow specialists are available to walk through custom requirements.
            </p>
          </div>
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs shadow-md shadow-amber-600/20 transition-all hover:scale-105"
          >
            <span>Start Free Trial</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FaqSection;

